import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { catchError, filter, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { createQueryParams, formatFullName } from '../helpers';
import { ICardV2 } from '../interfaces/card';
import { BoardService } from './board.service';
import { DialogService } from './dialog.service';
import { FileSaveService } from './file-save.service';
import { NotificationService } from './notifications.service';
import { StackedSidebarService } from './stacked-sidebar.service';
import { SidebarType } from '../components/layout/stacked-sidebar/stacked-sidebar.interface';
import {
  CardItemPayload,
  CardItemsBlock,
  FunnelWarehouseSettings,
  Named,
  ProductImportResult,
  ProductPayload,
  PropertyPayload,
  StockDocument,
  StockDocumentPayload,
  StockDocumentType,
  StockMovement,
  StockRow,
  TransferCloseResolution,
  VariantPayload,
  Warehouse,
  WarehouseCategory,
  WarehousePage,
  WarehousePayload,
  WarehouseProduct,
  WarehouseProperty,
  WarehouseQuery,
  WarehouseUnit,
} from '../components/warehouse/warehouse.types';
import { escapeHtml, formatQuantity, getApiError, getStockShortages } from '../components/warehouse/warehouse.utils';

/** An employee as `app-select` shows it with an avatar */
export interface ManagerOption extends Named {
  avatarUrl?: string;
}

/**
 * Warehouse accounting: its API (catalog, warehouses, stock, documents, card items), its sidebars and the feedback
 * of its screens: translated toasts, confirmations and readable API errors.
 */
@Injectable({ providedIn: 'root' })
export class WarehouseService {
  private readonly http = inject(HttpClient);
  private readonly boardService = inject(BoardService);
  private readonly dialogService = inject(DialogService);
  private readonly fileSaveService = inject(FileSaveService);
  private readonly notificationService = inject(NotificationService);
  private readonly stackedSidebarService = inject(StackedSidebarService);
  private readonly translateService = inject(TranslateService);

  // #region catalog

  /** A company without units gets the standard ones on the first request */
  readonly getUnits = (): Observable<WarehouseUnit[]> => this.request('GET', 'warehouse/unit');
  readonly saveUnit = (id: string | null, body: Omit<WarehouseUnit, 'id'>): Observable<WarehouseUnit> =>
    this.save('warehouse/unit', id, body);
  readonly getCategories = (): Observable<WarehouseCategory[]> => this.request('GET', 'warehouse/category');
  readonly saveCategory = (
    id: string | null,
    body: Pick<WarehouseCategory, 'name' | 'parent_id'>,
  ): Observable<unknown> => this.save('warehouse/category', id, body);
  readonly getProperties = (): Observable<WarehouseProperty[]> => this.request('GET', 'warehouse/property');
  readonly createProperty = (body: { name: string; values?: string[] }): Observable<WarehouseProperty> =>
    this.request('POST', 'warehouse/property', body);
  readonly updateProperty = (id: string, body: PropertyPayload): Observable<WarehouseProperty> =>
    this.request('PUT', `warehouse/property/${id}`, body);
  /** Returns the property with all its values */
  readonly addPropertyValue = (id: string, value: string): Observable<WarehouseProperty> =>
    this.request('POST', `warehouse/property/${id}/value`, { value });

  readonly getProducts = (query: WarehouseQuery): Observable<WarehousePage<WarehouseProduct>> =>
    this.request('GET', 'warehouse/product', null, query);
  readonly getProduct = (id: string): Observable<WarehouseProduct> => this.request('GET', `warehouse/product/${id}`);
  /** The currency of a product with stock history is locked (409) */
  readonly saveProduct = (id: string | null, body: ProductPayload): Observable<WarehouseProduct> =>
    this.save('warehouse/product', id, body);
  /** Variant requests return the whole product */
  readonly createVariant = (productId: string, body: VariantPayload): Observable<WarehouseProduct> =>
    this.request('POST', `warehouse/product/${productId}/variant`, body);
  readonly updateVariant = (id: string, body: VariantPayload): Observable<WarehouseProduct> =>
    this.request('PUT', `warehouse/variant/${id}`, body);
  /** CSV or XLSX: rows with the same name become variants of one product */
  readonly importProducts = (file: File): Observable<ProductImportResult> => {
    const body = new FormData();
    body.append('file', file, file.name);
    return this.request('POST', 'warehouse/product/import', body);
  };

  /** 409 *_IN_USE, *_HAS_HISTORY or CATEGORY_NOT_EMPTY while something uses it; a variant returns its product */
  readonly remove = <T = unknown>(
    entity: 'unit' | 'category' | 'property' | 'product' | 'variant' | 'warehouse' | 'document',
    id: string,
  ): Observable<T> => this.request('DELETE', `warehouse/${entity}/${id}`);

  // #endregion

  // #region warehouses, stock and documents

  /** All warehouses, archived ones included, when `isArchived` is not set */
  readonly getWarehouses = (isArchived?: boolean): Observable<Warehouse[]> =>
    this.request('GET', 'warehouse/warehouse', null, { is_archived: isArchived });
  readonly saveWarehouse = (id: string | null, body: WarehousePayload): Observable<Warehouse> =>
    this.save('warehouse/warehouse', id, body);
  readonly getStock = (query: WarehouseQuery): Observable<WarehousePage<StockRow>> =>
    this.request('GET', 'warehouse/stock', null, query);
  readonly getMovements = (query: WarehouseQuery): Observable<WarehousePage<StockMovement>> =>
    this.request('GET', 'warehouse/movement', null, query);

  /** The list has `lines_count` only, `lines` is empty */
  readonly getDocuments = (query: WarehouseQuery): Observable<WarehousePage<StockDocument>> =>
    this.request('GET', 'warehouse/document', null, query);
  readonly getDocument = (id: string): Observable<StockDocument> => this.request('GET', `warehouse/document/${id}`);
  /** Saves a draft, the stock does not change */
  readonly saveDocument = (id: string | null, body: StockDocumentPayload): Observable<StockDocument> =>
    this.save('warehouse/document', id, body);
  /**
   * post: moves the stock (409 INSUFFICIENT_STOCK on a warehouse without `allow_negative`); cancel: reverses it;
   * accept: a transfer takes what is in transit, all of it without `lines`; close: returns or loses the rest
   */
  readonly documentAction = (
    id: string,
    action: 'post' | 'cancel' | 'accept' | 'close',
    body: {
      lines?: { line_id: string; quantity: number }[];
      resolution?: TransferCloseResolution;
      reason?: string | null;
    } = {},
  ): Observable<StockDocument> => this.request('POST', `warehouse/document/${id}/${action}`, body);

  /**
   * Creates a document and posts it at once. A draft whose posting failed is deleted: trying again creates
   * a new one, and the old one must not double the stock when posted later.
   */
  postNewDocument(body: StockDocumentPayload): Observable<StockDocument> {
    return this.saveDocument(null, body).pipe(
      switchMap((document) =>
        this.documentAction(document.id, 'post').pipe(
          catchError((error: unknown) =>
            this.remove('document', document.id).pipe(
              catchError(() => of(null)),
              switchMap(() => throwError(() => error)),
            ),
          ),
        ),
      ),
    );
  }

  /** Downloads the stock or the movement journal with the filters of its list, without paging */
  exportList(list: 'stock' | 'movement', query: WarehouseQuery, format: 'csv' | 'xlsx'): Observable<void> {
    const params = createQueryParams({ ...query, page: null, take: null, format });
    return this.http
      .get(`${environment.baseURL}/warehouse/${list}/export`, { params, responseType: 'blob', observe: 'response' })
      .pipe(
        map((res) => {
          const name = /filename="?([^";]+)"?/i.exec(res.headers.get('Content-Disposition') ?? '')?.[1];
          this.fileSaveService.saveFile(res.body ?? new Blob(), name ?? `${list}.${format}`);
        }),
      );
  }

  // #endregion

  // #region card items

  /** Every item request returns the whole block: items, totals per currency and the written off flag */
  readonly getCardItems = (cardId: string): Observable<CardItemsBlock> =>
    this.request('GET', `board/card/${cardId}/item`);
  /** Price and currency come from the variant, the warehouse from the funnel settings */
  readonly addCardItem = (cardId: string, body: CardItemPayload): Observable<CardItemsBlock> =>
    this.request('POST', `board/card/${cardId}/item`, body);
  readonly updateCardItem = (cardId: string, itemId: string, body: CardItemPayload): Observable<CardItemsBlock> =>
    this.request('PUT', `board/card/${cardId}/item/${itemId}`, body);
  readonly removeCardItem = (cardId: string, itemId: string): Observable<CardItemsBlock> =>
    this.request('DELETE', `board/card/${cardId}/item/${itemId}`);
  /** The backend keeps the deal amount equal to the item total in the card currency; returns the card */
  readonly setPriceFromItems = (cardId: string, enabled: boolean): Observable<ICardV2> =>
    this.request('PUT', `board/card/${cardId}`, { is_price_from_items: enabled });
  readonly getFunnelSettings = (funnelId: string): Observable<FunnelWarehouseSettings> =>
    this.request('GET', `board/funnel/${funnelId}/warehouse-settings`);
  /** Works from the next card move, cards already in the columns are not recalculated */
  readonly saveFunnelSettings = (funnelId: string, body: FunnelWarehouseSettings): Observable<unknown> =>
    this.request('PUT', `board/funnel/${funnelId}/warehouse-settings`, body);

  // #endregion

  // #region sidebars, each emits whether something changed (the saved warehouse for the warehouse form)

  openProductForm(productId: string | null, categoryId: string | null = null): Observable<boolean | undefined> {
    return this.openSidebar(SidebarType.WarehouseProductForm, '1080px', { productId, categoryId });
  }

  openProductImport(): Observable<boolean | undefined> {
    return this.openSidebar(SidebarType.WarehouseProductImport, '560px', {});
  }

  openWarehouseForm(warehouse: Warehouse | null = null): Observable<Warehouse | undefined> {
    return this.openSidebar(SidebarType.WarehouseForm, '520px', { warehouse });
  }

  /** A new draft of `type` when there is no `documentId` */
  openDocument(documentId: string | null, type: StockDocumentType | null = null): Observable<boolean | undefined> {
    return this.openSidebar(SidebarType.WarehouseDocument, '1080px', { documentId, type });
  }

  openFunnelSettings(funnelId: string): Observable<unknown> {
    return this.openSidebar(SidebarType.BoardV3WarehouseSettings, '480px', { funnelId });
  }

  // #endregion

  // #region feedback

  t(key: string, params?: Record<string, unknown>): string {
    return String(this.translateService.instant(key, params));
  }

  success(key: string, params?: Record<string, unknown>): void {
    this.notificationService.showSuccess('', this.t(key, params));
  }

  /** Toast of a failure with its own text */
  fail(key: string, params?: Record<string, unknown>): void {
    this.notificationService.showError('', this.t(key, params));
  }

  /** Toast of an API error; `variantName` names the variants of a stock shortage, `contextKey` says what failed */
  error(error: unknown, variantName?: (variantId: string) => string | null, contextKey?: string): void {
    const message = this.errorMessage(error, variantName);
    this.notificationService.showError('', contextKey ? `${this.t(contextKey)}<br>${message}` : message);
  }

  /** Emits once the translated question is confirmed, right away when there is no question */
  confirm(key: string | null, params?: Record<string, unknown>): Observable<unknown> {
    if (!key) return of(true);
    return this.dialogService.openConfirm({ text: this.t(key, params), isTranslate: true }).pipe(filter(Boolean));
  }

  /**
   * Deletes after a confirmation (`<keys>delete_confirm`). One with stock history (409 *_HAS_HISTORY) can only be
   * archived: that is offered right then (`<keys>has_history`). Emits whether it was deleted rather than archived.
   */
  deleteOrArchive(
    keys: string,
    name: string,
    remove: Observable<unknown>,
    archive: Observable<unknown>,
  ): Observable<boolean> {
    return this.confirm(`${keys}delete_confirm`, { name }).pipe(
      switchMap(() => remove.pipe(map(() => true))),
      catchError((error: unknown) =>
        getApiError(error).code?.endsWith('_HAS_HISTORY')
          ? this.confirm(`${keys}has_history`).pipe(switchMap(() => archive.pipe(map(() => false))))
          : throwError(() => error),
      ),
    );
  }

  /** Archives after a confirmation, or restores */
  archiveProduct(product: WarehouseProduct, archive: boolean): Observable<WarehouseProduct> {
    return this.confirm(archive ? 'stock.product_form.archive_confirm' : null, { name: product.name }).pipe(
      switchMap(() => this.saveProduct(product.id, { is_archived: archive })),
      tap(() => this.success(archive ? 'stock.product_form.archived' : 'stock.product_form.restored')),
    );
  }

  /** Emits whether it was deleted rather than archived for its stock history */
  deleteProduct(product: WarehouseProduct): Observable<boolean> {
    const remove = this.remove('product', product.id);
    const archive = this.saveProduct(product.id, { is_archived: true });
    return this.deleteOrArchive('stock.product_form.', product.name, remove, archive).pipe(
      tap((deleted) => this.success(deleted ? 'stock.product_form.deleted' : 'stock.product_form.archived')),
    );
  }

  /** Active employees for the manager pickers; an empty list when they fail to load */
  getManagers(): Observable<ManagerOption[]> {
    return this.boardService.getManagers({ page: 1, take: 500 }).pipe(
      map((managers) =>
        managers.map((manager) => ({
          id: manager.id,
          name: formatFullName(manager.first_name, manager.last_name) || manager.email,
          avatarUrl: manager.avatar_url || undefined,
        })),
      ),
      catchError(() => of([])),
    );
  }

  /**
   * Readable text of an API error: `stock.errors.<CODE>.<reason>` or `stock.errors.<CODE>`. Shortages and the line of
   * an invalid document are listed with `<br>`, as toasts render HTML, so every value from the API is escaped.
   */
  errorMessage(error: unknown, variantName?: (variantId: string) => string | null): string {
    const { code, details = {} } = getApiError(error);
    const { reason, field, line } = details as { reason?: string; field?: string; line?: number };

    if (code === 'INSUFFICIENT_STOCK') {
      const lines = getStockShortages(error).map((shortage) =>
        this.t('stock.errors.shortage_line', {
          name: escapeHtml(variantName?.(shortage.variant_id) || this.t('stock.product')),
          warehouse: escapeHtml(shortage.warehouse_name),
          requested: formatQuantity(shortage.requested),
          available: formatQuantity(shortage.available),
        }),
      );
      return [this.t('stock.errors.INSUFFICIENT_STOCK'), ...lines].join('<br>');
    }
    if (code === 'STOCK_DOCUMENT_INVALID') {
      const params = { field: field ? this.t(`stock.document_field.${field}`) : '' };
      const text =
        this.find(`stock.errors.${code}.${reason}`, params) ?? this.t(`stock.errors.${code}.invalid`, params);
      return typeof line === 'number' ? `${this.t('stock.errors.line_prefix', { line: line + 1 })} ${text}` : text;
    }
    if (code === 'STOCK_DOCUMENT_STATUS_CONFLICT' && details['returned_by_document_id']) {
      return this.t('stock.errors.write_off_returned');
    }

    return (
      this.find(`stock.errors.${code}.${reason}`) ?? this.find(`stock.errors.${code}`) ?? this.t('errors.default_error')
    );
  }

  // #endregion

  /** The translation of the key, null when there is no text for it */
  private find(key: string, params?: Record<string, unknown>): string | null {
    const text: unknown = this.translateService.instant(key, params);
    return typeof text === 'string' && text !== key ? text : null;
  }

  private request<T>(method: string, path: string, body?: unknown, query?: WarehouseQuery): Observable<T> {
    return this.http
      .request<{ data: T }>(method, `${environment.baseURL}/${path}`, {
        body,
        params: query && createQueryParams(query),
      })
      .pipe(map((res) => res.data));
  }

  /** Creates the item of a list endpoint, updates it when there is an `id` */
  private save<T>(path: string, id: string | null, body: unknown): Observable<T> {
    return id ? this.request('PUT', `${path}/${id}`, body) : this.request('POST', path, body);
  }

  private openSidebar<T>(type: SidebarType, width: string, payload: object): Observable<T | undefined> {
    return this.stackedSidebarService.openSidebar({ type, width }, payload) as Observable<T | undefined>;
  }
}
